import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';

export default function Hero() {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // Honeycomb hexagons floating in background
  const honeycombs = [
    { size: 100, delay: 0, x: '15%', y: '20%', rotation: 30 },
    { size: 80, delay: 1, x: '75%', y: '15%', rotation: 60 },
    { size: 120, delay: 2, x: '85%', y: '65%', rotation: 90 },
    { size: 70, delay: 1.5, x: '10%', y: '75%', rotation: 45 },
    { size: 90, delay: 0.5, x: '60%', y: '80%', rotation: 15 },
    { size: 110, delay: 2.5, x: '40%', y: '10%', rotation: 75 },
  ];

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-amber-50 via-yellow-50 to-orange-50">
      {/* Honeycomb Pattern Overlay */}
      <div className="absolute inset-0 opacity-5" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='52' viewBox='0 0 60 52' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M30 0l15 8.66v17.32L30 34.64 15 25.98V8.66L30 0zm0 52l15-8.66V25.98L30 17.32l-15 8.66v17.36L30 52z' fill='%23f59e0b' fill-opacity='1'/%3E%3C/svg%3E")`,
        backgroundSize: '60px 52px'
      }}></div>

      {/* Animated Honey Drips */}
      <div className="absolute top-0 left-0 w-full h-32 overflow-hidden">
        {[...Array(8)].map((_, i) => (
          <motion.div
            key={i}
            initial={{ y: -100, opacity: 0 }}
            animate={{ 
              y: [0, 150, 150],
              opacity: [0, 0.6, 0],
            }}
            transition={{
              duration: 3 + i * 0.5,
              repeat: Infinity,
              delay: i * 0.8,
              ease: 'easeIn'
            }}
            className="absolute w-2 h-20 bg-gradient-to-b from-honey-400 to-amber-500 rounded-full"
            style={{ left: `${10 + i * 12}%` }}
          />
        ))}
      </div>

      {/* Floating Honeycomb Hexagons */}
      {honeycombs.map((hex, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, scale: 0, rotate: 0 }}
          animate={{
            opacity: [0.15, 0.3, 0.15],
            scale: [1, 1.1, 1],
            rotate: [hex.rotation, hex.rotation + 360],
            y: [0, -30, 0],
          }}
          transition={{
            duration: 15 + i * 2,
            repeat: Infinity,
            delay: hex.delay,
            ease: 'easeInOut',
          }}
          className="absolute"
          style={{
            width: hex.size,
            height: hex.size,
            left: hex.x,
            top: hex.y,
            clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)',
            background: 'linear-gradient(135deg, #f59e0b 0%, #f97316 100%)',
          }}
        />
      ))}

      {/* Honey Glow Orbs */}
      <motion.div
        animate={{
          scale: [1, 1.3, 1],
          opacity: [0.2, 0.4, 0.2],
        }}
        transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
        className="absolute top-1/4 left-1/4 w-96 h-96 bg-honey-400/30 rounded-full blur-3xl"
      />
      <motion.div
        animate={{
          scale: [1.2, 1, 1.2],
          opacity: [0.3, 0.5, 0.3],
        }}
        transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
        className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-amber-500/30 rounded-full blur-3xl"
      />

      {/* Interactive Cursor Hexagon */}
      <motion.div
        className="fixed pointer-events-none z-50 mix-blend-multiply"
        animate={{
          x: mousePosition.x - 20,
          y: mousePosition.y - 20,
        }}
        transition={{ type: 'spring', stiffness: 500, damping: 28 }}
        style={{
          width: 40,
          height: 40,
          clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)',
          background: 'linear-gradient(135deg, #f59e0b 0%, #f97316 100%)',
          opacity: 0.3,
        }}
      />

      {/* Main Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 text-center">
        {/* Honey Jar Icon with Dripping Effect */}
        <motion.div
          initial={{ scale: 0, y: -100 }}
          animate={{ scale: 1, y: 0 }}
          transition={{ duration: 1, type: 'spring', bounce: 0.6 }}
          className="mb-8 inline-block relative"
        >
          <motion.div
            animate={{
              y: [0, -15, 0],
            }}
            transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
            className="relative"
          >
            {/* Jar Container */}
            <div className="relative w-40 h-40">
              {/* Jar Body - Hexagonal Shape */}
              <div 
                className="absolute inset-0 bg-gradient-to-br from-honey-300 via-honey-400 to-amber-500 shadow-2xl"
                style={{
                  clipPath: 'polygon(30% 0%, 70% 0%, 100% 30%, 100% 70%, 70% 100%, 30% 100%, 0% 70%, 0% 30%)',
                }}
              >
                {/* Glass Shine Effect */}
                <div className="absolute inset-0 bg-gradient-to-br from-white/40 to-transparent" 
                  style={{
                    clipPath: 'polygon(30% 0%, 70% 0%, 100% 30%, 100% 70%, 70% 100%, 30% 100%, 0% 70%, 0% 30%)',
                  }}
                />
              </div>
              
              {/* Honey Drop Icon Inside */}
              <div className="absolute inset-0 flex items-center justify-center">
                <i className="ri-drop-fill text-6xl text-white drop-shadow-lg"></i>
              </div>
            </div>

            {/* Dripping Honey Animation */}
            <motion.div
              animate={{
                scaleY: [0, 1, 0],
                opacity: [0, 1, 0],
              }}
              transition={{ duration: 2, repeat: Infinity, repeatDelay: 1 }}
              className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-3 h-8 bg-gradient-to-b from-honey-400 to-amber-500 rounded-full origin-top"
            />

            {/* Hexagonal Ripples */}
            {[...Array(3)].map((_, i) => (
              <motion.div
                key={i}
                animate={{ 
                  scale: [1, 2, 2],
                  opacity: [0.5, 0.2, 0] 
                }}
                transition={{ 
                  duration: 2, 
                  repeat: Infinity,
                  delay: i * 0.6 
                }}
                className="absolute inset-0 border-4 border-honey-400"
                style={{
                  clipPath: 'polygon(30% 0%, 70% 0%, 100% 30%, 100% 70%, 70% 100%, 30% 100%, 0% 70%, 0% 30%)',
                }}
              />
            ))}
          </motion.div>
        </motion.div>

        {/* Main Headline */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.8 }}
        >
          <h1 className="text-7xl lg:text-8xl font-bold mb-6 leading-tight">
            <motion.span
              className="inline-block bg-gradient-to-r from-honey-600 via-amber-500 to-orange-500 bg-clip-text text-transparent"
              animate={{ backgroundPosition: ['0%', '100%', '0%'] }}
              transition={{ duration: 5, repeat: Infinity }}
              style={{ backgroundSize: '200% auto' }}
            >
              Learn. Earn.
            </motion.span>
            <br />
            <motion.span
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.6 }}
              className="text-charcoal-800"
            >
              Get Rewarded.
            </motion.span>
          </h1>
        </motion.div>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="text-xl text-gray-700 mb-12 max-w-3xl mx-auto leading-relaxed"
        >
          Complete missions, shop smarter, and redeem exclusive rewards from{' '}
          <strong className="text-honey-600">B.E. Wear</strong> and premium partners. Your journey to earning HMUs starts now.
        </motion.p>

        {/* CTA Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1 }}
          className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-16"
        >
          <motion.button
            whileHover={{ scale: 1.05, boxShadow: '0 20px 40px rgba(245, 158, 11, 0.4)' }}
            whileTap={{ scale: 0.95 }}
            className="px-10 py-5 bg-gradient-to-r from-honey-500 to-amber-500 text-white rounded-full font-bold text-lg shadow-xl hover:shadow-2xl transition-all whitespace-nowrap cursor-pointer"
          >
            Start Earning Now
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.05, borderColor: '#f59e0b' }}
            whileTap={{ scale: 0.95 }}
            className="px-10 py-5 border-3 border-charcoal-800 text-charcoal-800 rounded-full font-bold text-lg hover:bg-charcoal-800 hover:text-white transition-all whitespace-nowrap cursor-pointer"
          >
            Explore Missions
          </motion.button>
        </motion.div>

        {/* Hexagonal Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
          {[
            { icon: 'ri-user-line', value: '50K+', label: 'Active Users', delay: 1.2 },
            { icon: 'ri-coin-line', value: '2M+', label: 'HMUs Earned', delay: 1.4 },
            { icon: 'ri-gift-line', value: '10K+', label: 'Rewards Redeemed', delay: 1.6 },
          ].map((stat, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: stat.delay, type: 'spring' }}
              whileHover={{
                y: -10,
                boxShadow: '0 25px 50px rgba(245, 158, 11, 0.3)',
              }}
              className="relative bg-white/90 backdrop-blur-lg rounded-2xl p-8 shadow-xl border-2 border-honey-200 cursor-pointer overflow-hidden"
            >
              {/* Honeycomb Background Pattern */}
              <div className="absolute inset-0 opacity-5" style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='35' viewBox='0 0 40 35' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M20 0l10 5.77v11.55L20 23.09 10 17.32V5.77L20 0z' fill='%23f59e0b'/%3E%3C/svg%3E")`,
              }}></div>
              
              <motion.div
                animate={{ rotate: [0, 360] }}
                transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
                className="relative w-16 h-16 flex items-center justify-center bg-gradient-to-br from-honey-400 to-amber-500 mb-4 mx-auto"
                style={{
                  clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)',
                }}
              >
                <i className={`${stat.icon} text-3xl text-white`}></i>
              </motion.div>
              <div className="relative text-4xl font-bold text-charcoal-800 mb-2">{stat.value}</div>
              <div className="relative text-sm text-gray-600">{stat.label}</div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1, y: [0, 10, 0] }}
        transition={{ delay: 2, duration: 2, repeat: Infinity }}
        className="absolute bottom-10 left-1/2 transform -translate-x-1/2"
      >
        <i className="ri-arrow-down-line text-3xl text-honey-600"></i>
      </motion.div>
    </section>
  );
}
